import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Search, X, Library, Grid3X3 } from 'lucide-react';
import { FixedSizeGrid as VirtualGrid } from 'react-window';
import { iconLibraryService, IconMetadata } from '@/services/icons/IconLibraryService';
import { IconLibrary } from '@/types';

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

// ---------------------------------------------------------------------------
// Icon cell (lazy-loaded)
// ---------------------------------------------------------------------------
const IconCell = React.memo(({
  icon,
  isSelected,
  onSelect,
}: {
  icon: IconMetadata;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const [Comp, setComp] = useState<React.ComponentType<any> | null>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    let mounted = true;
    const lib = icon.library === 'lucide' ? IconLibrary.LUCIDE : IconLibrary.RADIX;
    iconLibraryService.loadIcon(icon.name, lib).then((c) => {
      if (mounted && c) setComp(() => c);
    });
    return () => { mounted = false; };
  }, [icon.name, icon.library]);

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex flex-col items-center justify-center rounded-md transition-colors w-full h-full ${
        isSelected
          ? 'bg-emerald-50 ring-1 ring-emerald-400'
          : 'hover:bg-gray-50'
      }`}
      title={icon.displayName}
    >
      <div className="flex items-center justify-center w-7 h-7">
        {Comp ? (
          <Comp size={20} color="currentColor" className="text-gray-700" />
        ) : (
          <div className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
        )}
      </div>
      {hovered && (
        <span className="text-[9px] text-gray-500 truncate w-full text-center px-0.5 leading-tight">
          {icon.displayName}
        </span>
      )}
    </button>
  );
});
IconCell.displayName = 'IconCell';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface IconPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIcon?: string;
  selectedLibrary?: IconLibrary;
  onSelect: (iconName: string, library: IconLibrary) => void;
  /** Lock library filter (e.g. 'lucide' for components that only support Lucide icons) */
  libraryFilter?: 'lucide' | 'radix';
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export function IconPickerModal({
  isOpen,
  onClose,
  selectedIcon,
  selectedLibrary,
  onSelect,
  libraryFilter,
}: IconPickerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [library, setLibrary] = useState<'all' | 'lucide' | 'radix'>(libraryFilter ?? 'all');
  const [category, setCategory] = useState('all');
  const searchRef = useRef<HTMLInputElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridSize, setGridSize] = useState({ width: 500, height: 400 });

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Reset filters when modal opens and auto-focus search
  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setLibrary(libraryFilter ?? 'all');
    setCategory('all');
    const t = setTimeout(() => searchRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isOpen, libraryFilter]);

  // Measure grid container
  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const el = gridContainerRef.current;
      if (el) {
        setGridSize({ width: el.clientWidth, height: el.clientHeight });
      }
    };
    // Measure after dialog animation
    const t = setTimeout(measure, 150);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [isOpen]);

  const categories = useMemo(() => ['all', ...iconLibraryService.getCategories()], []);

  const filteredIcons = useMemo(() => {
    const opts: any = {};
    if (debouncedSearch) opts.query = debouncedSearch;
    if (category !== 'all') opts.category = category;
    if (library !== 'all') opts.library = library === 'lucide' ? IconLibrary.LUCIDE : IconLibrary.RADIX;
    return iconLibraryService.searchIcons(opts);
  }, [debouncedSearch, category, library]);

  const handleSelect = useCallback((iconName: string, lib: IconLibrary) => {
    onSelect(iconName, lib);
    onClose();
  }, [onSelect, onClose]);

  // Grid config
  const CELL_SIZE = 56;
  const COLUMN_COUNT = Math.max(1, Math.floor(gridSize.width / CELL_SIZE));
  const rowCount = Math.ceil(filteredIcons.length / COLUMN_COUNT);

  const Cell = useCallback(
    ({ columnIndex, rowIndex, style }: any) => {
      const idx = rowIndex * COLUMN_COUNT + columnIndex;
      const icon = filteredIcons[idx];
      if (!icon) return null;
      const isSel =
        selectedIcon === icon.name &&
        selectedLibrary === (icon.library === 'lucide' ? IconLibrary.LUCIDE : IconLibrary.RADIX);
      return (
        <div style={style} className="p-0.5">
          <IconCell
            icon={icon}
            isSelected={isSel}
            onSelect={() => {
              const l = icon.library === 'lucide' ? IconLibrary.LUCIDE : IconLibrary.RADIX;
              handleSelect(icon.name, l);
            }}
          />
        </div>
      );
    },
    [filteredIcons, selectedIcon, selectedLibrary, COLUMN_COUNT, handleSelect],
  );

  // Sidebar button helper (matches PageBrowserModal)
  const sidebarBtn = (active: boolean) =>
    `flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs w-full text-left transition-colors ${
      active ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Choose Icon</h2>
            <p className="text-[11px] text-gray-500">
              {filteredIcons.length} icon{filteredIcons.length !== 1 ? 's' : ''} available
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 pb-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-8 text-xs placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Main content: sidebar + grid */}
        <div className="flex flex-1 min-h-0" style={{ height: '60vh' }}>
          {/* Sidebar */}
          <div className="w-40 border-r border-gray-100 flex flex-col py-2 px-2 shrink-0 overflow-y-auto">
            {/* Library section (hidden when locked to a single library) */}
            {!libraryFilter && (
              <>
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider px-2 mb-1">Library</p>
                <button onClick={() => setLibrary('all')} className={sidebarBtn(library === 'all')}>
                  <Library className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate flex-1">All</span>
                  <span className="text-[10px] text-gray-400">{filteredIcons.length}</span>
                </button>
                <button onClick={() => setLibrary('lucide')} className={sidebarBtn(library === 'lucide')}>
                  <span className="w-3.5 text-center text-[10px] shrink-0">L</span>
                  <span className="truncate flex-1">Lucide</span>
                </button>
                <button onClick={() => setLibrary('radix')} className={sidebarBtn(library === 'radix')}>
                  <span className="w-3.5 text-center text-[10px] shrink-0">R</span>
                  <span className="truncate flex-1">Radix</span>
                </button>

                <div className="border-t border-gray-100 my-1.5" />
              </>
            )}

            {/* Category section */}
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider px-2 mb-1">Category</p>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={sidebarBtn(category === cat)}
              >
                <Grid3X3 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate flex-1">
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </span>
              </button>
            ))}
          </div>

          {/* Icon grid */}
          <div ref={gridContainerRef} className="flex-1 overflow-hidden">
            {filteredIcons.length > 0 ? (
              <VirtualGrid
                columnCount={COLUMN_COUNT}
                columnWidth={CELL_SIZE}
                height={gridSize.height}
                rowCount={rowCount}
                rowHeight={CELL_SIZE}
                width={gridSize.width}
                className="scrollbar-thin"
              >
                {Cell}
              </VirtualGrid>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <Search className="mx-auto h-10 w-10 opacity-40 mb-3" />
                  <p className="text-sm">No icons found</p>
                  <p className="text-xs mt-1">Try adjusting your search or filters</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
